/**
 * SkeletonExamples.tsx
 * Comprehensive examples of HeroUI Skeleton component
 * 
 * Showcases all HeroUI Skeleton features using our shared components
 */

import React, { useState, useEffect } from 'react';
import { PlaygroundPageLayout } from '../shared';
import { Skeleton } from '@enfomentis/ui';
import { Card, CardBody, CardHeader } from '@enfomentis/ui';
import { Button } from '@enfomentis/ui';
import { Badge } from '@enfomentis/ui';


export const SkeletonExamples: React.FC = () => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isCardLoaded, setIsCardLoaded] = useState(false);
  const [isTableLoaded, setIsTableLoaded] = useState(false);
  const [isProfileLoaded, setIsProfileLoaded] = useState(false);

  // Auto-toggle for demo
  useEffect(() => {
    const interval = setInterval(() => {
      setIsLoaded(prev => !prev);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  const simulateCardLoad = () => {
    setIsCardLoaded(false);
    setTimeout(() => setIsCardLoaded(true), 2000);
  };

  const simulateTableLoad = () => {
    setIsTableLoaded(false);
    setTimeout(() => setIsTableLoaded(true), 3000);
  };

  const simulateProfileLoad = () => {
    setIsProfileLoaded(false);
    setTimeout(() => setIsProfileLoaded(true), 2500);
  };

  return (
    <PlaygroundPageLayout>
      <div className="space-y-8">
        
        {/* Basic Usage */}
        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Basic Usage</h3>
          <Card>
            <CardBody>
              <div className="space-y-6">
                <div>
                  <h4 className="text-sm font-medium text-text-secondary mb-3">Text Skeleton</h4>
                  <div className="space-y-2">
                    <Skeleton>
                      <div className="h-4 w-full rounded bg-default-200" />
                    </Skeleton>
                    <Skeleton>
                      <div className="h-4 w-4/5 rounded bg-default-200" />
                    </Skeleton>
                    <Skeleton>
                      <div className="h-4 w-3/5 rounded bg-default-200" />
                    </Skeleton>
                  </div>
                </div>
                
                <div>
                  <h4 className="text-sm font-medium text-text-secondary mb-3">Avatar Skeleton</h4>
                  <div className="flex items-center space-x-4">
                    <Skeleton>
                      <div className="w-12 h-12 rounded-full bg-default-300" />
                    </Skeleton>
                    <div className="space-y-2 flex-1">
                      <Skeleton>
                        <div className="h-4 w-1/4 rounded bg-default-200" />
                      </Skeleton>
                      <Skeleton>
                        <div className="h-3 w-1/3 rounded bg-default-200" />
                      </Skeleton>
                    </div>
                  </div>
                </div>
                
                <div>
                  <h4 className="text-sm font-medium text-text-secondary mb-3">Card Skeleton</h4>
                  <Skeleton>
                    <div className="h-24 w-full rounded-lg bg-default-300" />
                  </Skeleton>
                </div>
              </div>
            </CardBody>
          </Card>
        </div>

        {/* Loading States */}
        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Loading States</h3>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <h4 className="text-lg font-semibold text-text-primary">Auto-Toggle Demo</h4>
                  <Badge color={isLoaded ? "success" : "warning"} variant="flat">
                    {isLoaded ? "Loaded" : "Loading"}
                  </Badge>
                </div>
              </CardHeader>
              <CardBody>
                <Skeleton isLoaded={isLoaded}>
                  <div className="space-y-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center">
                        <span className="text-white text-sm font-medium">JS</span>
                      </div>
                      <div>
                        <div className="font-medium text-text-primary">John Smith</div>
                        <div className="text-sm text-text-secondary">Portfolio Manager</div>
                      </div>
                    </div>
                    <div className="text-text-primary">
                      Successfully loaded user profile with all trading statistics and portfolio metrics.
                    </div>
                  </div>
                </Skeleton>
              </CardBody>
            </Card>

            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <h4 className="text-lg font-semibold text-text-primary">Manual Control</h4>
                  <Button 
                    onPress={simulateCardLoad}
                    color="primary"
                    size="sm"
                    isDisabled={!isCardLoaded && isCardLoaded !== false}
                  >
                    Load Content
                  </Button>
                </div>
              </CardHeader>
              <CardBody>
                <Skeleton isLoaded={isCardLoaded}>
                  <div className="space-y-3">
                    <div className="text-lg font-semibold text-text-primary">Trading Dashboard</div>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <div className="text-text-secondary">P&L Today</div>
                        <div className="text-success font-medium">+$1,247</div>
                      </div>
                      <div>
                        <div className="text-text-secondary">Win Rate</div>
                        <div className="text-primary font-medium">73.5%</div>
                      </div>
                    </div>
                  </div>
                </Skeleton>
              </CardBody>
            </Card>
          </div>
        </div>

        {/* Animation Control */}
        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Animation Control</h3>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            
            <Card>
              <CardHeader>
                <h4 className="text-lg font-semibold text-text-primary">With Animation (Default)</h4>
              </CardHeader>
              <CardBody>
                <div className="space-y-3">
                  <Skeleton>
                    <div className="h-4 w-full rounded bg-default-200" />
                  </Skeleton>
                  <Skeleton>
                    <div className="h-4 w-4/5 rounded bg-default-200" />
                  </Skeleton>
                  <Skeleton>
                    <div className="h-4 w-3/5 rounded bg-default-200" />
                  </Skeleton>
                </div>
              </CardBody>
            </Card>

            <Card>
              <CardHeader>
                <h4 className="text-lg font-semibold text-text-primary">Without Animation</h4>
              </CardHeader>
              <CardBody>
                <div className="space-y-3">
                  <Skeleton disableAnimation>
                    <div className="h-4 w-full rounded bg-default-200" />
                  </Skeleton>
                  <Skeleton disableAnimation>
                    <div className="h-4 w-4/5 rounded bg-default-200" />
                  </Skeleton>
                  <Skeleton disableAnimation>
                    <div className="h-4 w-3/5 rounded bg-default-200" />
                  </Skeleton>
                </div>
              </CardBody>
            </Card>
          </div>
        </div>

        {/* Trading Context Examples */}
        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Trading Context</h3>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <h4 className="text-lg font-semibold text-text-primary">Portfolio Table</h4>
                  <Button 
                    onPress={simulateTableLoad}
                    color="secondary"
                    size="sm"
                    isDisabled={!isTableLoaded && isTableLoaded !== false}
                  >
                    Load Positions
                  </Button>
                </div>
              </CardHeader>
              <CardBody>
                <Skeleton isLoaded={isTableLoaded}>
                  <div className="space-y-3">
                    {/* Table header */}
                    <div className="grid grid-cols-4 gap-4 text-xs font-medium text-text-secondary uppercase">
                      <div>Symbol</div>
                      <div>Quantity</div>
                      <div>Price</div>
                      <div>P&L</div>
                    </div>
                    {/* Table rows */}
                    <div className="space-y-2">
                      <div className="grid grid-cols-4 gap-4 text-sm">
                        <div className="font-medium">AAPL</div>
                        <div>150</div>
                        <div>$178.25</div>
                        <div className="text-success">+$423</div>
                      </div>
                      <div className="grid grid-cols-4 gap-4 text-sm">
                        <div className="font-medium">MSFT</div>
                        <div>100</div>
                        <div>$367.89</div>
                        <div className="text-success">+$287</div>
                      </div>
                      <div className="grid grid-cols-4 gap-4 text-sm">
                        <div className="font-medium">GOOGL</div>
                        <div>50</div>
                        <div>$141.33</div>
                        <div className="text-loss">-$156</div>
                      </div>
                    </div>
                  </div>
                </Skeleton>
                {!isTableLoaded && (
                  <div className="space-y-3">
                    {/* Table header skeleton */}
                    <div className="grid grid-cols-4 gap-4">
                      <Skeleton><div className="h-3 w-12 rounded bg-default-200" /></Skeleton>
                      <Skeleton><div className="h-3 w-16 rounded bg-default-200" /></Skeleton>
                      <Skeleton><div className="h-3 w-10 rounded bg-default-200" /></Skeleton>
                      <Skeleton><div className="h-3 w-8 rounded bg-default-200" /></Skeleton>
                    </div>
                    {/* Table rows skeleton */}
                    <div className="space-y-2">
                      {[1, 2, 3].map((row) => (
                        <div key={row} className="grid grid-cols-4 gap-4">
                          <Skeleton><div className="h-4 w-12 rounded bg-default-200" /></Skeleton>
                          <Skeleton><div className="h-4 w-8 rounded bg-default-200" /></Skeleton>
                          <Skeleton><div className="h-4 w-14 rounded bg-default-200" /></Skeleton>
                          <Skeleton><div className="h-4 w-12 rounded bg-default-200" /></Skeleton>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardBody>
            </Card>

            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <h4 className="text-lg font-semibold text-text-primary">Trader Profile</h4>
                  <Button 
                    onPress={simulateProfileLoad}
                    color="success"
                    size="sm"
                    isDisabled={!isProfileLoaded && isProfileLoaded !== false}
                  >
                    Load Profile
                  </Button>
                </div>
              </CardHeader>
              <CardBody>
                <Skeleton isLoaded={isProfileLoaded}>
                  <div className="space-y-4">
                    <div className="flex items-center space-x-4">
                      <div className="w-16 h-16 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
                        <span className="text-white text-lg font-bold">AT</span>
                      </div>
                      <div>
                        <div className="text-lg font-semibold text-text-primary">Alex Thompson</div>
                        <div className="text-text-secondary">Senior Options Trader</div>
                        <div className="text-sm text-success">Active • Online</div>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <div className="text-text-secondary text-sm">Total Trades</div>
                        <div className="font-semibold text-text-primary">1,247</div>
                      </div>
                      <div>
                        <div className="text-text-secondary text-sm">Success Rate</div>
                        <div className="font-semibold text-success">84.3%</div>
                      </div>
                    </div>
                  </div>
                </Skeleton>
                {!isProfileLoaded && (
                  <div className="space-y-4">
                    <div className="flex items-center space-x-4">
                      <Skeleton><div className="w-16 h-16 rounded-full bg-default-300" /></Skeleton>
                      <div className="space-y-2">
                        <Skeleton><div className="h-5 w-32 rounded bg-default-200" /></Skeleton>
                        <Skeleton><div className="h-4 w-24 rounded bg-default-200" /></Skeleton>
                        <Skeleton><div className="h-3 w-20 rounded bg-default-200" /></Skeleton>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-1">
                        <Skeleton><div className="h-3 w-16 rounded bg-default-200" /></Skeleton>
                        <Skeleton><div className="h-4 w-12 rounded bg-default-200" /></Skeleton>
                      </div>
                      <div className="space-y-1">
                        <Skeleton><div className="h-3 w-18 rounded bg-default-200" /></Skeleton>
                        <Skeleton><div className="h-4 w-10 rounded bg-default-200" /></Skeleton>
                      </div>
                    </div>
                  </div>
                )}
              </CardBody>
            </Card>
          </div>
        </div>

        {/* Complex Layouts */}
        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Complex Layout Skeletons</h3>
          <div className="space-y-6">
            
            {/* Dashboard Skeleton */}
            <Card>
              <CardHeader>
                <h4 className="text-lg font-semibold text-text-primary">Dashboard Loading</h4>
              </CardHeader>
              <CardBody>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  
                  {/* Stat Cards */}
                  {[1, 2, 3].map((card) => (
                    <div key={card} className="p-4 border border-border rounded-lg space-y-3">
                      <div className="flex items-center justify-between">
                        <Skeleton><div className="h-4 w-20 rounded bg-default-200" /></Skeleton>
                        <Skeleton><div className="w-6 h-6 rounded bg-default-300" /></Skeleton>
                      </div>
                      <Skeleton><div className="h-8 w-16 rounded bg-default-200" /></Skeleton>
                      <Skeleton><div className="h-3 w-24 rounded bg-default-200" /></Skeleton>
                    </div>
                  ))}
                </div>
                
                <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
                  
                  {/* Chart Area */}
                  <div className="space-y-3">
                    <Skeleton><div className="h-4 w-32 rounded bg-default-200" /></Skeleton>
                    <Skeleton><div className="h-48 w-full rounded-lg bg-default-300" /></Skeleton>
                  </div>
                  
                  {/* Recent Activity */}
                  <div className="space-y-3">
                    <Skeleton><div className="h-4 w-28 rounded bg-default-200" /></Skeleton>
                    <div className="space-y-3">
                      {[1, 2, 3, 4].map((item) => (
                        <div key={item} className="flex items-center space-x-3">
                          <Skeleton><div className="w-8 h-8 rounded-full bg-default-300" /></Skeleton>
                          <div className="flex-1 space-y-1">
                            <Skeleton><div className="h-3 w-32 rounded bg-default-200" /></Skeleton>
                            <Skeleton><div className="h-3 w-20 rounded bg-default-200" /></Skeleton>
                          </div>
                          <Skeleton><div className="h-3 w-12 rounded bg-default-200" /></Skeleton>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </CardBody>
            </Card>

            {/* List Skeleton */}
            <Card>
              <CardHeader>
                <h4 className="text-lg font-semibold text-text-primary">Trade History Loading</h4>
              </CardHeader>
              <CardBody>
                <div className="space-y-4">
                  {[1, 2, 3, 4, 5].map((trade) => (
                    <div key={trade} className="flex items-center space-x-4 p-3 border border-border rounded">
                      <Skeleton><div className="w-10 h-10 rounded bg-default-300" /></Skeleton>
                      <div className="flex-1 space-y-2">
                        <div className="flex items-center justify-between">
                          <Skeleton><div className="h-4 w-16 rounded bg-default-200" /></Skeleton>
                          <Skeleton><div className="h-4 w-20 rounded bg-default-200" /></Skeleton>
                        </div>
                        <div className="flex items-center space-x-4">
                          <Skeleton><div className="h-3 w-12 rounded bg-default-200" /></Skeleton>
                          <Skeleton><div className="h-3 w-16 rounded bg-default-200" /></Skeleton>
                          <Skeleton><div className="h-3 w-14 rounded bg-default-200" /></Skeleton>
                        </div>
                      </div>
                      <Skeleton><div className="h-6 w-16 rounded bg-default-200" /></Skeleton>
                    </div>
                  ))}
                </div>
              </CardBody>
            </Card>
          </div>
        </div>

        {/* Different Content Types */}
        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Content Type Variations</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            
            {/* Article/Blog Post */}
            <Card>
              <CardHeader>
                <h4 className="text-lg font-semibold text-text-primary">Article</h4>
              </CardHeader>
              <CardBody>
                <div className="space-y-4">
                  <Skeleton><div className="h-32 w-full rounded-lg bg-default-300" /></Skeleton>
                  <div className="space-y-2">
                    <Skeleton><div className="h-5 w-full rounded bg-default-200" /></Skeleton>
                    <Skeleton><div className="h-5 w-4/5 rounded bg-default-200" /></Skeleton>
                  </div>
                  <div className="space-y-2">
                    <Skeleton><div className="h-3 w-full rounded bg-default-200" /></Skeleton>
                    <Skeleton><div className="h-3 w-full rounded bg-default-200" /></Skeleton>
                    <Skeleton><div className="h-3 w-3/5 rounded bg-default-200" /></Skeleton>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Skeleton><div className="w-6 h-6 rounded-full bg-default-300" /></Skeleton>
                    <Skeleton><div className="h-3 w-16 rounded bg-default-200" /></Skeleton>
                  </div>
                </div>
              </CardBody>
            </Card>

            {/* Media Card */}
            <Card>
              <CardHeader>
                <h4 className="text-lg font-semibold text-text-primary">Media Card</h4>
              </CardHeader>
              <CardBody>
                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <Skeleton><div className="w-12 h-12 rounded-full bg-default-300" /></Skeleton>
                    <div className="space-y-1">
                      <Skeleton><div className="h-4 w-24 rounded bg-default-200" /></Skeleton>
                      <Skeleton><div className="h-3 w-16 rounded bg-default-200" /></Skeleton>
                    </div>
                  </div>
                  <Skeleton><div className="h-40 w-full rounded-lg bg-default-300" /></Skeleton>
                  <div className="space-y-2">
                    <Skeleton><div className="h-4 w-full rounded bg-default-200" /></Skeleton>
                    <Skeleton><div className="h-4 w-2/3 rounded bg-default-200" /></Skeleton>
                  </div>
                  <div className="flex justify-between items-center pt-2">
                    <div className="flex space-x-4">
                      <Skeleton><div className="h-3 w-8 rounded bg-default-200" /></Skeleton>
                      <Skeleton><div className="h-3 w-12 rounded bg-default-200" /></Skeleton>
                    </div>
                    <Skeleton><div className="h-3 w-10 rounded bg-default-200" /></Skeleton>
                  </div>
                </div>
              </CardBody>
            </Card>

            {/* Form Skeleton */}
            <Card>
              <CardHeader>
                <h4 className="text-lg font-semibold text-text-primary">Form</h4>
              </CardHeader>
              <CardBody>
                <div className="space-y-4">
                  <div className="space-y-1">
                    <Skeleton><div className="h-3 w-16 rounded bg-default-200" /></Skeleton>
                    <Skeleton><div className="h-10 w-full rounded bg-default-300" /></Skeleton>
                  </div>
                  <div className="space-y-1">
                    <Skeleton><div className="h-3 w-20 rounded bg-default-200" /></Skeleton>
                    <Skeleton><div className="h-10 w-full rounded bg-default-300" /></Skeleton>
                  </div>
                  <div className="space-y-1">
                    <Skeleton><div className="h-3 w-24 rounded bg-default-200" /></Skeleton>
                    <Skeleton><div className="h-20 w-full rounded bg-default-300" /></Skeleton>
                  </div>
                  <div className="flex space-x-3 pt-2">
                    <Skeleton><div className="h-9 w-20 rounded bg-default-300" /></Skeleton>
                    <Skeleton><div className="h-9 w-16 rounded bg-default-200" /></Skeleton>
                  </div>
                </div>
              </CardBody>
            </Card>
          </div>
        </div>

      </div>
    </PlaygroundPageLayout>
  );
};